import { useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { InlineEditShell } from "./InlineEditShell";
import { useInlineEdit } from "./useInlineEdit";

/**
 * Texto editável por clique. Enter salva, Esc cancela, blur salva.
 * Em modo `multiline`, Enter quebra linha e o salvamento fica no blur.
 */
export default function InlineEditText({
  value,
  onCommit,
  canEdit,
  label,
  children,
  className,
  inputClassName,
  multiline = false,
  maxLength,
  placeholder,
  disabledReason,
  hidePencil,
  externalTrigger,
}: {
  value: string | null;
  onCommit: (next: string | null) => Promise<void>;
  canEdit: boolean;
  label: string;
  /** Como o valor aparece quando não está em edição. */
  children: React.ReactNode;
  className?: string;
  inputClassName?: string;
  multiline?: boolean;
  maxLength?: number;
  placeholder?: string;
  disabledReason?: string;
  hidePencil?: boolean;
  /** Abre a edição de fora (ex.: botão-lápis ao lado de um link). */
  externalTrigger?: { open: boolean; onClose: () => void };
}) {
  const edit = useInlineEdit<string | null>({
    value,
    onCommit,
    disabled: !canEdit,
    // Vazio e null são a mesma coisa para o usuário; sem isso, limpar um campo
    // já nulo geraria uma gravação (e uma linha de histórico) à toa.
    isEqual: (a, b) => (a ?? "").trim() === (b ?? "").trim(),
  });

  const ref = useRef<HTMLInputElement & HTMLTextAreaElement>(null);

  useEffect(() => {
    if (edit.isEditing) {
      ref.current?.focus();
      ref.current?.select();
    }
  }, [edit.isEditing]);

  // Gatilho externo: o display continua sendo um link navegável e o lápis
  // vizinho é quem abre a edição.
  useEffect(() => {
    if (externalTrigger?.open && !edit.isEditing) edit.start();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalTrigger?.open]);

  useEffect(() => {
    if (!edit.isEditing && externalTrigger?.open) externalTrigger.onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [edit.isEditing]);

  // Continua aberto no estado de erro, preservando o que foi digitado.
  if (edit.isEditing) {
    const Field = multiline ? Textarea : Input;
    return (
      <div className="relative">
        <Field
          ref={ref as never}
          value={edit.draft ?? ""}
          maxLength={maxLength}
          placeholder={placeholder}
          onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
            edit.setDraft(e.target.value)
          }
          onKeyDown={(e: React.KeyboardEvent) => {
            // Em textarea, Enter é quebra de linha — só Esc e blur controlam.
            if (multiline && e.key === "Enter") return;
            edit.fieldProps.onKeyDown(e);
          }}
          onBlur={edit.fieldProps.onBlur}
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
          className={cn(
            "h-auto py-1",
            edit.hasError && "border-destructive",
            inputClassName,
          )}
          aria-label={label}
          aria-invalid={edit.hasError || undefined}
        />
        {edit.hasError && (
          <p role="alert" className="mt-0.5 text-[10px] text-destructive">
            {edit.error} · Esc para descartar
          </p>
        )}
      </div>
    );
  }

  return (
    <InlineEditShell
      canEdit={canEdit}
      status={edit.status}
      error={edit.error}
      label={label}
      triggerProps={edit.triggerProps}
      className={className}
      disabledReason={disabledReason}
      hidePencil={hidePencil}
    >
      {children}
    </InlineEditShell>
  );
}
